import React, { useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import paths from 'app/paths'
import { useAccess } from 'contexts/access'
import { useAbsenceStats } from 'contexts/absence-stats'
import AbsenceEntriesProvider, { useAbsenceEntries } from 'contexts/absence-entries'
import { useNavigate } from 'react-router-dom'
import { useQueryResolveItem } from 'hooks/resolve-item'
import { useLocationStateIntent } from 'hooks/location-intent'
import { useAppSignal } from 'hooks/app-signal'
import { useTypeNameFormatter } from 'pages/absence/components/type-name'
import { useInterval } from 'hooks/interval'
import { isAfter, endOfDay } from 'date-fns'
import { refreshAbsenceEntries, refreshAbsenceStats } from 'pages/absence/utilities'
import { getDate } from 'utilities/date-time'
import OngoingEntry from 'pages/absence/components/ongoing-entry'
import GroupedEntries from 'pages/absence/components/grouped-entries'
import Modal from 'modals/dialog'
import ActingEntry from 'pages/absence/modals/entry'
import Error from 'pages/absence/modals/error'

const FutureEntries = ({ user, setAddingToCalendar, showWorkdaysAffected, hasFetchedAbsenceTypes, salt }) => {
    const { formatMessage } = useIntl()

    const { check } = useAccess()
    const absenceAdmin = check('absence:manage')

    const { fetchStats } = useAbsenceStats()

    const {
        entries,

        fetchEntry,
        endEntry,
        removeEntry,
        error,
        resetError
    } = useAbsenceEntries()


    const typeNameFormatter = useTypeNameFormatter()

    const navigate = useNavigate()

    const [acting, setActing] = useState(null)

    const [now, setNow] = useState(new Date)
    useInterval(() => setNow(new Date), 60 * 60 * 1000)

    useQueryResolveItem({
        items: entries,
        fetch: fetchEntry,
        onResolved: (entry, clearQuery) => {
            entry && viewAction(entry)?.onClick()
            clearQuery()
        }
    })

    useLocationStateIntent({
        intent: 'register-absence',
        action: type => setActing({
            mode: 'edit',
            ...(type ? { entry: type } : null),
            user
        })
    })

    useAppSignal({
        namespace: 'absence:entry:act',
        action: act => setActing(act)
    })

    const viewAction = useCallback(entry => {
        if(user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:view`,
            label: formatMessage({
                id: 'action_view_details',
                defaultMessage: 'View details'
            }),
            effect: 'neutral',
            onClick: () => setActing({
                entry,
                mode: 'view'
            })
        }
    }, [salt])

    const editAction = useCallback(entry => {
        const { status } = entry

        // const approver = permissionLevel === 'approve'
        // const pending = status === 'pending'

        if(user?.status?.active === false || status === 'canceled') {
            return null
        }

        const action = {
            salt: `${salt}:edit`,
            effect: 'neutral'
        }

        // if(!pending && (!!end && isAfter(now, end)) && !approver) {
        //     return {
        //         ...action,
        //         label: formatMessage({
        //             id: 'action_edit_locked',
        //             defaultMessage: 'Locked for editing'
        //         }),
        //         helptext: formatMessage({
        //             id: 'action_edit_locked_caption',
        //             defaultMessage: 'Talk to an administrator to make changes'
        //         }),
        //         disabled: true
        //     }
        // }

        return {
            ...action,
            label: formatMessage({
                id: 'action_edit_details',
                defaultMessage: 'Edit details'
            }),
            onClick: () => setActing({
                entry,
                mode: 'edit',
                callback: fetchStats
            })
        }
    }, [salt])

    const viewAbsenceCalendarAction = useCallback(({ start, end, id }) => {
        if(!end || user?.status?.active === false) {
            return null
        }

        return ({
            salt: `${salt}:absence-calendar`,
            label: formatMessage({
                id: 'absence_action_view_in_calendar',
                defaultMessage: 'View in absence calendar'
            }),
            effect: 'neutral',
            onClick: () => navigate(paths.absence.calendar, {
                state: {
                    start,
                    entryFocus: id
                },
                replace: true
            })
        })
    }, [salt])

    const addToCalendarAction = useCallback(({ start, end, type }) => {
        if(!end || user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:calendar`,
            label: formatMessage({
                id: 'absence_action_add_to_external_calendar',
                defaultMessage: 'Add to external calendar'
            }),
            effect: 'neutral',
            onClick: () => setAddingToCalendar({
                start,
                end,
                title: typeNameFormatter(type)
            })
        }
    }, [salt])

    const endNowAction = useCallback(entry => {
        const {
            start,
            end
        } = entry

        if(!isAfter(now, start) || !!end || user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:end-now`,
            label: formatMessage({
                id: 'action_end_now',
                defaultMessage: 'End now'
            }),
            onClick: async () => {
                const result = await endEntry(entry)

                if(result.response?.errorCode?.includes('overlapping') && absenceAdmin) {
                    setActing({
                        entry: {
                            ...entry,
                            end: getDate()
                        },
                        overrideTouched: true,
                        mode: 'edit'
                    })
                } else {
                    await fetchStats()
                }

                return result
            },
            effect: 'neutral'
        }
    }, [salt])

    const removeAction = useCallback(({ permissionLevel, status, start, id }) => {
        if(user?.status?.active === false) {
            return null
        }

        const approver = permissionLevel === 'approve'
        const rejected = status === 'rejected'
        const historical = isAfter(now, endOfDay(start))
        const disabled = historical && !approver && !rejected

        return {
            salt: `${salt}:delete`,
            label: formatMessage({
                id: 'action_delete',
                defaultMessage: 'Delete'
            }),
            prompt: {
                question: formatMessage({
                    id: 'absence_delete_prompt_question',
                    defaultMessage: 'Confirm removing this absence'
                }),
                confirmText: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete'
                })
            },
            effect: 'destructive',
            onClick: async () => {
                const result = await removeEntry(id)
                await fetchStats()
                setActing(null)

                return result
            },
            disabled
        }
    }, [salt])

    const actions = {
        view: viewAction,
        edit: editAction,
        viewAbsenceCalendar: viewAbsenceCalendarAction,
        addToCalendar: addToCalendarAction,
        endNow: endNowAction,
        remove: removeAction
    }

    return (
        <>
            <OngoingEntry
                user={user}
                setActing={setActing}
                setAddingToCalendar={setAddingToCalendar}
                showWorkdaysAffected={showWorkdaysAffected}
                salt={salt} />
            <GroupedEntries
                user={user}
                actions={actions}
                setActing={setActing}
                setAddingToCalendar={setAddingToCalendar}
                showWorkdaysAffected={showWorkdaysAffected}
                salt={salt} />
            <Modal
                show={!!acting}
                dismiss={() => setActing(null)}
                className="scrollable"
                salt={`${salt}:act`}>
                <ActingEntry
                    {...acting}
                    actions={actions}
                    dismiss={() => setActing(null)}
                    onDone={updated => {
                        refreshAbsenceEntries()
                        refreshAbsenceStats()

                        void setActing(acting => {
                            if(updated) {
                                return {
                                    ...acting,
                                    entry: {
                                        ...acting.entry,
                                        ...updated
                                    },
                                    mode: 'view'
                                }
                            }

                            return null
                        }
                    )}}
                    showMarkedDates={true}
                    hasFetchedAbsenceTypes={hasFetchedAbsenceTypes}
                    salt={`${salt}:act`} />
            </Modal>
            <Modal
                show={!!error}
                dismiss={resetError}
                className="scrollable"
                salt={`${salt}:error`}>
                <Error error={error} />
            </Modal>
        </>
    )
}

export default props => (
    <AbsenceEntriesProvider
        exhaustiveFetch={true}
        filter={{ users: ['me'] }}
        paging={{ limit: 10 }}>
        <FutureEntries {...props} />
    </AbsenceEntriesProvider>
)
